import { ReactNode, useEffect, useRef } from 'react';
import { View, ViewProps } from '@shared/components/View';
import { getController } from '@shared/util/timeout';
import styles from './AnchoredTooltip.module.css';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface AnchoredTooltipProps extends ViewProps {
  anchorName?: string;
  tooltip: ReactNode;
  position?: TooltipPosition;
  disabled?: boolean;
}

const defaultTooltipAnchorName = '--tooltip-anchor';

export function AnchoredTooltip({
  anchorName = defaultTooltipAnchorName,
  className,
  children,
  tooltip,
  position = 'top',
  disabled = false,
  ...rest
}: AnchoredTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) {
      return;
    }

    const firstChild = ref.current?.firstChild;
    const tooltipEl = tooltipRef.current;
    if (!(firstChild instanceof HTMLElement) || !tooltipEl) {
      return;
    }

    const { signal, abort } = getController();
    const show = () => tooltipEl.showPopover();
    const hide = () => tooltipEl.hidePopover();

    firstChild.style.anchorName = anchorName;
    firstChild.addEventListener('mouseenter', show, { signal });
    firstChild.addEventListener('mouseleave', hide, { signal });
    return () => {
      abort();
      hide();
    };
  }, [disabled, anchorName]);

  return (
    <View
      {...rest}
      ref={ref}
      className={[styles.anchoredTooltip, className]}
      data-position={position}
    >
      {children}
      {tooltip && (
        <div
          ref={tooltipRef}
          className={styles.tooltip}
          style={{ positionAnchor: anchorName }}
          popover="manual"
        >
          {tooltip}
        </div>
      )}
    </View>
  );
}
